import { Button, Form, Image } from "react-bootstrap";
import { React, useState, useRef } from "react";
import styled from "styled-components";
import {useNavigate, useParams} from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { createNewBlog } from "../../api/axios";
import { showToast } from "../../Utills/toast";
import FormGroup from "../../Components/Common/FormGroup";
import removeIcon from "../../Assets/remove-image-photo.svg";

// Validation schema
const validationSchema = Yup.object({
  blog_img: Yup.mixed().required("Image is required"),
  blog_title: Yup.string().required("Title is required"),
  blog_content: Yup.string().required("Content is required"),
  blog_readMore: Yup.string().required("Read More is required"),
});

function CreateBlog() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const handleImageChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue("blog_img", file);
    }
  };

  const handleImageDelete = (setFieldValue) => {
    // Clear the image and reset file input
    setFieldValue("blog_img", "");
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);

    // Additional validation for character limits
    if (values.blog_title.length > 50) {
      showToast({
        type: 'error',
        message: 'Title must be less than 50 characters',
      });
      setSaving(false);
      return;
    }

    if (values.blog_content.length > 300) {
      showToast({
        type: 'error',
        message: 'Content must be less than 300 characters',
      });
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("blog_title", values.blog_title);
    formData.append("blog_content", values.blog_content);
    formData.append("blog_readMore", values.blog_readMore);
    formData.append("partner_id", partnerId);

    // Add image data to formData if it exists
    if (values.blog_img instanceof File) {
      formData.append("blog_img", values.blog_img);
    }

    try {
      const res = await createNewBlog(formData);
      
      if (res.status === 200) {
        showToast({
          type: 'success',
          message: 'Blog has been created successfully!',
        });
        if (res.data.data && res.data.data.id) {
          navigate(`/${partnerId}/resources/update/${res.data.data.id}`);
        }
      } else {
        showToast({
          type: 'error',
          message: 'Failed to create blog. Please try again.',
        });
      }
      setSaving(false);
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to create blog. Please try again.',
      });
      
      setSaving(false);
    }
  };

  const handleClick = () => {
    navigate(`/${partnerId}/resources`);
  };

  const getInitialValues = () => ({
    blog_img: "",
    blog_title: "",
    blog_content: "",
    blog_readMore: "",
  });

  return (
    <ContentWrapper>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, getFieldProps, setFieldValue, values, errors, touched }) => (
          <Form onSubmit={handleSubmit} noValidate>
            <SliderDiv>
              <Title>Create a new blog</Title>
              
              {/* Image Upload Section */}
              <FormGroup 
                label='Image' 
                requiredField
                error={touched.blog_img && errors.blog_img}
              >
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, setFieldValue)}
                  style={{ display: "none" }}
                  ref={inputRef}
                  name="blog_img"
                />
                
                {/* Show uploaded image (File object) - NEW IMAGE */}
                {values.blog_img instanceof File ? (
                  <ImageWrapper>
                    <UploadImage>
                      <BlogImage
                        src={URL.createObjectURL(values.blog_img)}
                        alt="blog_img"
                        onClick={() => inputRef.current.click()}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = 0.8;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.opacity = 1;
                        }}
                      />
                    </UploadImage>
                    {/* Always show delete icon for new images */}
                      {values.blog_img && values.blog_img !== values.old_blog_img && (
                      <RemoveButtonWrapper>
                        <BootstrapButton
                          type="button"
                        onClick={() => handleImageDelete(setFieldValue)}
                      >
                        <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                      </BootstrapButton>
                    </RemoveButtonWrapper>)}
                  </ImageWrapper>
                ) : (
                    <Button 
                      type="button"
                      className="mb-0"
                      onClick={() => inputRef.current.click()}
                      variant={"primary"}
                    > 
                      Upload the Image
                    </Button>
                  
                )}
              </FormGroup>

              {/* Title Field */}
              <FormGroup 
                error={touched.blog_title && errors.blog_title} 
                label='Title'
                requiredField
              >
                <Form.Control
                  type="text"
                  placeholder="Enter Blog Title"
                  maxLength="50"
                  {...getFieldProps("blog_title")}
                  isInvalid={touched.blog_title && errors.blog_title}
                />       
              </FormGroup>

              {/* Content Field */}
              <FormGroup 
                error={touched.blog_content && errors.blog_content} 
                label='Content'
                requiredField
              >
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter Blog Content"
                  maxLength={300}
                  {...getFieldProps("blog_content")}
                  isInvalid={touched.blog_content && errors.blog_content}
                />
              </FormGroup>

              {/* Read More Field */}
              <FormGroup 
                error={touched.blog_readMore && errors.blog_readMore} 
                label='Read More'
                requiredField
              >
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter Blog Read More"
                  {...getFieldProps("blog_readMore")}
                  isInvalid={touched.blog_readMore && errors.blog_readMore}
                />
              </FormGroup>

              {/* Button Section */}
              <ButtonWrapper className="mt-3">
                <Button variant="secondary" onClick={handleClick}>
                  Go Back
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </ButtonWrapper>
            </SliderDiv>
          </Form>
        )}
      </Formik>
    </ContentWrapper>
  );
}

export default CreateBlog;

const ContentWrapper = styled.div`
   @media (min-width:576px) {
    padding: 1rem;
    }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 15vw;
  @media (max-width: 576px) {
    max-width: 60vw;
  }
`;

const BlogImage = styled.img`
  max-width: 15vw;
  cursor: pointer;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width: 576px) {
    max-width: 60vw;
  }
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
    top: -17px;
    right: -14px;
    @media (max-width:576px) {
     top: -9px;
    right: -17px;
  }
`;

const BootstrapButton = styled.button`
  margin: 0 !important;
  background-color: #FFFFFF;
  padding: 5px 7px;
  border: 1px solid #dc3545;
  border-radius: 25px;
  cursor: pointer;
  &:hover {
    background: #fffeff;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px;
  }
  @media (max-width: 576px) {
    padding: 2px 4px;
  }
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
`;

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;
const UploadImage = styled.div`
  max-width: 15vw;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
   max-width: 90vw;
  }    
`;